import * as React from 'react'

import { computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import { ISharedDelivery } from '~/client/graph/types.generated'
import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import ResponsibilitiesPanel from '~/client/src/desktop/components/ResponsibilitiesPanel/ResponsibilitiesPanel'
import desktopRoutes from '~/client/src/desktop/constants/desktopRoutes'
import DeliveryActionTypes from '~/client/src/desktop/enums/DeliveryActionTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import DeliveryDetails from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails'
import * as Icons from '~/client/src/shared/components/Icons'
import SharedDeliveryActions from '~/client/src/shared/components/SharedEntityActions/SharedDeliveryActions'
import { SharedEntityActionTypes } from '~/client/src/shared/components/SharedEntityActions/SharedEntityActions'
import { getTransitionPath } from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import DeliveryAssignmentsStore from '~/client/src/shared/stores/domain/DeliveryAssignments.store'
import SharedDeliveriesStore from '~/client/src/shared/stores/domain/SharedDeliveries.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import DesktopDeliveryViewStore from '../../DesktopDeliveryView.store'
import DeliveriesListStore from '../DeliveriesList/DeliveriesList.store'
import DeliveryActionSideBase from './components/DeliveryActionSideBase'
import DeliveryBulkEditor from './components/DeliveryBulkEditor/DeliveryBulkEditor'
import DeliveryBulkUpdater from './components/DeliveryBulkUpdater/DeliveryBulkUpdater'

// localization: translated

interface IProps {
  desktopDeliveryViewStore: DesktopDeliveryViewStore
  deliveriesListStore: DeliveriesListStore
  state: DesktopInitialState

  deliveryAssignmentsStore?: DeliveryAssignmentsStore
  common?: DesktopCommonStore
  sharedDeliveriesStore?: SharedDeliveriesStore
}

interface IRouteParams {
  deliveryId?: string
}

@inject('deliveryAssignmentsStore', 'common', 'sharedDeliveriesStore')
@observer
class DeliveryActionSideWrapper extends React.Component<
  IProps & RouteComponentProps<IRouteParams>
> {
  @observable private deliveriesListStore: DeliveriesListStore = null

  private get requestedDeliveryId(): string {
    return this.props.match.params.deliveryId
  }

  public constructor(props: IProps & RouteComponentProps<IRouteParams>) {
    super(props)

    this.props.desktopDeliveryViewStore.prepareViewWhenFollowingDirectLink(
      this.requestedDeliveryId,
    )
    this.deliveriesListStore = this.props.deliveriesListStore
    makeObservable(this)
  }

  private handleLinkClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault()
    if (url) {
      window.open(url, '_blank')
    }
  }

  private handleCallTo = (email: string) => {
    if (email) {
      window.location.href = `tel:${email}`
    }
  }

  private handleMailTo = (email: string) => {
    if (email) {
      window.location.href = `mailto:${email}`
    }
  }

  private handleActionClick = (
    actionType: SharedEntityActionTypes,
    sharedDelivery: ISharedDelivery,
    e: React.MouseEvent,
  ) => {
    const { responsible, projectUrl, locationsFrom, locationsTo } =
      sharedDelivery
    switch (actionType) {
      case SharedEntityActionTypes.GO_TO_PROJECT:
        this.handleLinkClick(e, projectUrl)
        break
      case SharedEntityActionTypes.GO_TO_TO_LOCATION:
        this.handleLinkClick(e, locationsTo[0].locationUrl)
        break
      case SharedEntityActionTypes.GO_TO_FROM_LOCATION:
        this.handleLinkClick(e, locationsFrom[0].locationUrl)
        break
      case SharedEntityActionTypes.CALL_ADMIN:
        this.handleCallTo(responsible?.phoneNumber)
        break
      case SharedEntityActionTypes.MAIL_ADMIN:
        this.handleMailTo(responsible?.email)
        break
      case SharedEntityActionTypes.GO_TO_SETTINGS:
        this.handleLinkClick(e, this.settingUrl)
        break
    }
  }

  private renderSharedDeliveryActions = () => {
    const sharedDelivery = this.props.sharedDeliveriesStore.list.find(
      d => d.id === this.requestedDeliveryId,
    )
    if (!sharedDelivery) return null

    return (
      <SharedDeliveryActions
        entity={sharedDelivery}
        companyName={sharedDelivery.companyName}
        onActionClick={this.handleActionClick}
        projectId={sharedDelivery.projectFromId}
      />
    )
  }

  public render() {
    const { activeActionType } = this.props.desktopDeliveryViewStore

    if (!activeActionType) {
      return null
    }

    const {
      state,
      common,
      desktopDeliveryViewStore,
      deliveriesListStore,
      deliveryAssignmentsStore,
    } = this.props

    const { hideDeliveryDetails, resetActionType } = desktopDeliveryViewStore

    let title = ''
    let content = null
    let shouldShowHeader = true
    let header = null

    switch (activeActionType) {
      case DeliveryActionTypes.NEW:
      case DeliveryActionTypes.VIEW:
        shouldShowHeader = false
        content = (
          <DeliveryDetails
            initProjectId={state.activeProject.id}
            backClicked={hideDeliveryDetails}
            FileInputType={DesktopFileInput}
            displayedDeliveryId={this.requestedDeliveryId}
            onCreationCb={common._displayDeliveryDetailsView}
          />
        )
        break
      case DeliveryActionTypes.VIEW_SHARED:
        shouldShowHeader = true
        header = this.renderSharedEntityHeader()
        content = this.renderSharedDeliveryActions()
        break

      case DeliveryActionTypes.UPDATE:
        title = Localization.translator.updateDeliveryStatus
        content = (
          <DeliveryBulkUpdater
            deliveriesListStore={deliveriesListStore}
            onUpdated={this.handleUpdate}
          />
        )
        break
      case DeliveryActionTypes.ASSIGN:
        shouldShowHeader = false
        content = (
          <ResponsibilitiesPanel
            selectedEntitiesIds={this.selectedDeliveriesIds}
            domainStore={deliveryAssignmentsStore}
            onClose={resetActionType}
          />
        )
        break

      case DeliveryActionTypes.EDIT:
        title = Localization.translator.bulkEdit
        content = (
          <DeliveryBulkEditor
            selectedDeliveriesIds={this.selectedDeliveriesIds}
            onUpdated={this.handleUpdate}
          />
        )
    }

    return (
      <DeliveryActionSideBase
        store={desktopDeliveryViewStore}
        title={title}
        subTitle={EMPTY_STRING}
        content={content}
        shouldShowHeader={shouldShowHeader}
        header={header}
      />
    )
  }

  private renderSharedEntityHeader = () => {
    return (
      <div className="row pa10 pl20 bb-light-grey">
        <div className="no-grow col y-center" onClick={this.handleUpdate}>
          <Icons.Cross className="row shared-entity-cross-icon pointer" />
        </div>
        <div className="col x-center ml10 mr30">
          <div className="text large">
            {Localization.translator.workflowDetails}
          </div>
        </div>
      </div>
    )
  }

  @computed
  private get selectedDeliveriesIds(): string[] {
    return this.deliveriesListStore.selectedInstances.map(d => d.id)
  }

  private handleUpdate = () => {
    const { deliveriesListStore, desktopDeliveryViewStore } = this.props
    deliveriesListStore.resetSelection()
    desktopDeliveryViewStore.resetActionType()
  }

  public get settingUrl(): string {
    return (
      location.origin +
      getTransitionPath(
        desktopRoutes.PROJECT_DETAILS,
        this.props.state.activeProject.code,
      )
    )
  }
}

export default withRouter(DeliveryActionSideWrapper)
